/**
 * MintStatusModal
 *
 * Modal displaying mint progress and status.
 * Shows progress bar, current status, and completion message.
 */

import React from 'react';
import { Button } from '../../ui/components/Button';
import { FeatherCheck, FeatherAlertCircle, FeatherBell } from '@subframe/core';
import { type MintStatus, getMintStatusDisplay } from '../../api/drops';

export interface MintStatusModalProps {
  open: boolean;
  onClose: () => void;
  mintStatus: MintStatus | null;
  quantity: number;
  isFree?: boolean;
}

export const MintStatusModal: React.FC<MintStatusModalProps> = ({
  open,
  onClose,
  mintStatus,
  quantity,
  isFree = false,
}) => {
  if (!open) return null;

  const status = mintStatus?.mint.status || 'payment_pending';
  const statusDisplay = getMintStatusDisplay(status, isFree);
  const isComplete = status === 'completed';
  const isFailed = status === 'failed' || status === 'expired';
  const isProcessing = !isComplete && !isFailed;

  // Get progress step
  const getProgressStep = () => {
    switch (status) {
      case 'payment_pending':
        return 1;
      case 'payment_confirmed':
        return 2;
      case 'minting':
        return 3;
      case 'minted':
        return 4;
      case 'transferring':
        return 5;
      case 'completed':
        return 6;
      default:
        return 1;
    }
  };

  const progressStep = getProgressStep();
  const totalSteps = 6;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={isProcessing ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative bg-default-background rounded-lg border border-neutral-border shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="p-6 border-b border-neutral-border">
          <h2 className="text-heading-3 font-heading-3 text-default-font">
            Minting {quantity > 1 ? `${quantity} NFTs` : 'NFT'}
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isFailed ? 'bg-error-500' : isComplete ? 'bg-success-500' : 'bg-brand-500'
                }`}
                style={{ width: `${(progressStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3 mb-6">
            {isProcessing && (
              <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            )}
            {isComplete && (
              <div className="w-6 h-6 rounded-full bg-success-500 flex items-center justify-center">
                <FeatherCheck className="w-4 h-4 text-white" />
              </div>
            )}
            {isFailed && (
              <div className="w-6 h-6 rounded-full bg-error-500 flex items-center justify-center">
                <FeatherAlertCircle className="w-4 h-4 text-white" />
              </div>
            )}
            <span className={`text-body-bold font-body-bold ${
              isFailed ? 'text-error-600' : isComplete ? 'text-success-600' : 'text-default-font'
            }`}>
              {statusDisplay.label}
            </span>
          </div>

          {/* Success Message */}
          {isComplete && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-success-500 bg-success-500/10 mb-6">
              <FeatherBell className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-body text-default-font">
                  You'll receive transfer offer{quantity > 1 ? 's' : ''} for your {quantity > 1 ? `${quantity} NFTs` : 'NFT'} shortly.
                </p>
                <p className="text-caption text-subtext-color mt-1">
                  Check your notifications to accept and claim your NFT{quantity > 1 ? 's' : ''}.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {mintStatus?.mint.error_message && (
            <div className="p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 mb-6">
              <p className="text-body text-error-700 dark:text-error-400">
                {mintStatus.mint.error_message}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isProcessing && (
          <div className="p-6 border-t border-neutral-border">
            <Button
              variant="neutral-secondary"
              size="large"
              onClick={onClose}
              className="w-full"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
